import { useEffect, useState } from 'react';
import { useLocale, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';

import { useModalState } from 'hooks/useModalState/useModalState';
import { AppMessages } from 'i18n/messages';
import { downloadIssuedCardsReport } from 'api/reports/reports';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { IssuedCardsReportForm } from './IssuedCardsReport.types';
import { IssuedCardsReport } from './IssuedCardsReport';

export const IssuedCardsReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const formMethods = useForm<IssuedCardsReportForm>({
    defaultValues: {
      blocked: 'true',
    },
  });

  const { nazwisko, numerKarty, blocked, komorka } = formMethods.watch();

  const {
    onDownload: onDownloadPermissionsSummaryReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadIssuedCardsReport,
    buildRequestBody: (emails) => ({
      nazwisko,
      numerKarty,
      blocked: blocked === 'true' ? true : false,
      komorki: komorka?.id ? [komorka?.id] : [],
      selectedEmails: emails,
    }),
    onEmailModalClose,
    reportNameId: AppMessages['reports.issuedCardsReport'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleConfirmEmails = (emails: string[]) => {
    // Save selected emails for persistence
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    // Trigger the download/send
    onDownloadPermissionsSummaryReport(emails);
  };

  useEffect(() => {
    if (isModalOpen) {
      formMethods.reset({
        blocked: 'false',
      });
    } else {
      // Reset error and selected emails when modal closes
      resetError();
      setSelectedEmails([]);
    }
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <IssuedCardsReport
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        onDownload={onDownloadPermissionsSummaryReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.issuedCardsReport'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
